/**
 * Auth store — tracks the Firebase user, the Firestore user profile, and the
 * resolved role. The auth listener (`initAuthListener`) is started once from
 * `main.tsx` and keeps the store in sync with Firebase's onAuthStateChanged.
 * On every sign-in it calls POST /users/me/bootstrap, which creates the
 * /users/{uid} doc if missing and returns the latest profile either way.
 */
import {
  onAuthStateChanged,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from 'firebase/auth';
import { type User as AppUser, type UserRole } from '@cracked-mug/shared';
import { create } from 'zustand';
import { auth } from '../firebase';
import { bootstrapUser } from '../services/usersApi';
import { resetSocket } from '../services/socket';
import { useCartStore } from './cartStore';

export interface AuthState {
  firebaseUser: FirebaseUser | null;
  appUser: AppUser | null;
  role: UserRole | null;
  isLoading: boolean;
  error: string | null;
  signOut: () => Promise<void>;
  /**
   * Replace the cached profile after a /users/me PATCH so the rest of the
   * app (header, profile-completeness guard, etc.) sees the new state
   * without waiting for another bootstrap.
   */
  setAppUser: (user: AppUser) => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  firebaseUser: null,
  appUser: null,
  role: null,
  isLoading: true,
  error: null,
  signOut: async () => {
    await firebaseSignOut(auth);
  },
  setAppUser: (user) => set({ appUser: user, role: user.role }),
}));

let unsubscribe: (() => void) | null = null;

/** Bootstrap the Firebase auth listener. Call once from `main.tsx`. */
export function initAuthListener(): void {
  if (unsubscribe) {
    unsubscribe();
  }

  unsubscribe = onAuthStateChanged(auth, (fbUser) => {
    void handleAuthChange(fbUser);
  });
}

async function handleAuthChange(fbUser: FirebaseUser | null): Promise<void> {
  // Drop any socket opened under a previous identity so the next getSocket()
  // reconnects with the current user's token. Prevents a prior signed-in user
  // on a shared device from leaving an authenticated socket (e.g. one still in
  // the kitchen room, which carries PII) open for the next user.
  resetSocket();

  if (!fbUser) {
    // Signed out (explicitly or via token revocation): drop any cart left in
    // localStorage so the next person to sign in on a shared device starts
    // clean instead of inheriting the previous teacher's cart. A signed-in
    // user reloading the page fires with their user object (not null), so
    // their own cart still persists across reloads.
    useCartStore.getState().clear();
    useAuthStore.setState({
      firebaseUser: null,
      appUser: null,
      role: null,
      isLoading: false,
      error: null,
    });
    return;
  }

  try {
    const profile = await bootstrapUser();
    useAuthStore.setState({
      firebaseUser: fbUser,
      appUser: profile,
      role: profile.role,
      isLoading: false,
      error: null,
    });
  } catch (err) {
    // Bootstrap failed — surface the error but keep the user signed in.
    // RoleGuard treats `role: null` as "redirect to home" so the user can't
    // enter protected areas while in a broken state.
    useAuthStore.setState({
      firebaseUser: fbUser,
      appUser: null,
      role: null,
      isLoading: false,
      error: err instanceof Error ? err.message : 'Failed to load user profile',
    });
  }
}
